import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { LOGTABLE } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Columns3, ExternalLink, RefreshCcw, X, Sparkles, ChevronDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { handleReauth } from "@/lib/reauth";
import {
  DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const COLUMNS = [
  { key: "received_at", label: "Date" },
  { key: "received_time", label: "Time" },
  { key: "parent_name", label: "Parent" },
  { key: "parent_email", label: "Parent Email" },
  { key: "student_name", label: "Student" },
  { key: "subject", label: "Subject" },
  { key: "topic", label: "Topic" },
  { key: "summary", label: "Summary" },
  { key: "action_items", label: "Action Items" },
  { key: "deadlines", label: "Deadlines" },
  { key: "contact_info", label: "Contact Info" },
  { key: "urgency", label: "Urgency" },
  { key: "response_status", label: "Status" },
  { key: "teacher_notes", label: "Notes" },
  { key: "permalink", label: "Email" },
];

const DEFAULT_VISIBLE = new Set([
  "received_at","received_time","parent_name","student_name","subject","topic","summary",
  "action_items","urgency","response_status","teacher_notes","permalink",
]);

const URGENCIES = ["All", "High", "Normal", "Low"];
const TOPICS = ["All", "Academic", "Behavioral", "Attendance", "Health", "Scheduling", "Other"];
const STATUSES = ["All", "Needs Reply", "Replied", "No Action Needed"];

const TONES = [
  { key: "Warm", label: "Warm & reassuring", hint: "For worried or first-time parents" },
  { key: "Direct", label: "Direct & concise", hint: "For routine logistical notes" },
  { key: "Detailed", label: "Detailed with specifics", hint: "For academic, IEP, complex issues" },
];

function urgencyBadge(u) {
  const styles = {
    High: "bg-[#fde5ef] text-[#ec197f] border-transparent",
    Normal: "bg-[#F3F1EC] text-[#5D6D63] border-transparent",
    Low: "bg-[#e7f5fb] text-[#26abe2] border-transparent",
  };
  return <Badge className={`rounded-full px-3 ${styles[u] || styles.Normal}`}>{u || "Normal"}</Badge>;
}

function topicBadge(t) {
  return <Badge variant="outline" className="rounded-full border-[#E8E6DF] text-[#5D6D63] font-normal">{t || "Other"}</Badge>;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString([], { dateStyle: "medium" });
  } catch { return iso; }
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { timeStyle: "short" });
  } catch { return ""; }
}

function ChipGroup({ label, options, value, onChange, testidPrefix }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#8B9891] pr-1">{label}</span>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            data-testid={`${testidPrefix}-${opt.replace(/\s+/g,'-').toLowerCase()}`}
            onClick={() => onChange(opt)}
            className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${
              active
                ? "bg-[#971a8c] border-[#971a8c] text-white"
                : "bg-white border-[#E8E6DF] text-[#5D6D63] hover:border-[#971a8c] hover:text-[#971a8c]"
            }`}
          >{opt}</button>
        );
      })}
    </div>
  );
}

export default function LogEntries() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [urgency, setUrgency] = useState("All");
  const [topic, setTopic] = useState("All");
  const [status, setStatus] = useState("All");
  const [visible, setVisible] = useState(new Set(DEFAULT_VISIBLE));
  const [syncing, setSyncing] = useState(false);
  const [draftingId, setDraftingId] = useState(null);
  const [tone, setTone] = useState(() => {
    if (typeof window === "undefined") return "Warm";
    return localStorage.getItem("ca:reply-tone") || "Warm";
  });
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("ca:reply-tone", tone);
  }, [tone]);

  const params = useMemo(() => ({
    q: q || undefined,
    urgency: urgency !== "All" ? urgency : undefined,
    topic: topic !== "All" ? topic : undefined,
    status: status !== "All" ? status : undefined,
    limit: 500,
  }), [q, urgency, topic, status]);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const r = await api.get("/entries", { params: p });
      setRows(r.data);
    } catch (e) {
      toast.error("Failed to load entries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(params), 250);
    return () => clearTimeout(t);
  }, [params, load]);

  const updateEntry = async (entry_id, patch) => {
    setRows((r) => r.map((row) => row.entry_id === entry_id ? { ...row, ...patch } : row));
    try {
      await api.patch(`/entries/${entry_id}`, patch);
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const syncFromSheet = async () => {
    setSyncing(true);
    toast.message("Reading your Google Sheet…");
    try {
      const r = await api.post("/scan/sync-from-sheet");
      const restored = r.data.restored || 0;
      const updated = r.data.updated || 0;
      const parts = [];
      if (restored > 0) parts.push(`${restored} restored from sheet`);
      if (updated > 0) parts.push(`${updated} updated`);
      const msg = parts.length ? `Sync complete · ${parts.join(" · ")}` : "Sync complete · no changes";
      toast.success(msg);
      load(params);
    } catch (e) {
      handleReauth(e, "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const draftReply = async (entry_id) => {
    setDraftingId(entry_id);
    toast.message(`Drafting a ${tone.toLowerCase()} reply with Claude…`);
    try {
      const r = await api.post(`/entries/${entry_id}/draft-reply`, { tone });
      const preview = r.data.reply_preview || "";
      toast.success(`Draft saved to Gmail (${r.data.tone || tone})`, {
        description: preview.length > 140 ? preview.slice(0, 140) + "…" : (preview || "Open Gmail Drafts to review and send."),
        duration: 10000,
        action: {
          label: "Open Drafts",
          onClick: () => window.open(r.data.drafts_url, "_blank", "noopener"),
        },
      });
    } catch (e) {
      handleReauth(e, "Draft failed");
    } finally {
      setDraftingId(null);
    }
  };

  const deleteEntry = async (entry_id) => {
    // Optimistic: remove from state immediately, revert on error.
    const prev = rows;
    setRows((r) => r.filter((row) => row.entry_id !== entry_id));
    try {
      await api.delete(`/entries/${entry_id}`);
      toast.success("Entry removed from your log and Google Sheet.");
    } catch (e) {
      setRows(prev);
      handleReauth(e, "Delete failed");
    }
  };

  const clearFilters = () => { setQ(""); setUrgency("All"); setTopic("All"); setStatus("All"); };
  const activeFilters = (q ? 1 : 0) + (urgency !== "All" ? 1 : 0) + (topic !== "All" ? 1 : 0) + (status !== "All" ? 1 : 0);

  return (
    <div data-testid={LOGTABLE.root}>
      <div className="mb-8 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8B9891] mb-2">Parent Contact Log</p>
          <h1 className="font-heading text-4xl tracking-tight text-[#1C2321] font-medium">All entries</h1>
          <p className="mt-2 text-[#5D6D63] max-w-2xl">
            Every parent message ContactAssist has logged. Edits sync between here and your Google Sheet.
          </p>
        </div>
        <Button
          data-testid="log-sync-btn"
          onClick={syncFromSheet}
          disabled={syncing}
          variant="outline"
          className="h-11 rounded-md border-[#E8E6DF] bg-white hover:border-[#971a8c] hover:text-[#971a8c]"
        >
          <RefreshCcw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing…" : "Sync from Sheet"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B9891]" />
          <Input
            data-testid={LOGTABLE.search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search parent, student, subject, topic…"
            className="pl-9 h-11 rounded-md border-[#E8E6DF] bg-white"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid={LOGTABLE.columnToggle} variant="outline" className="h-11 rounded-md border-[#E8E6DF] bg-white">
              <Columns3 className="h-4 w-4 mr-2" /> Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {COLUMNS.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.key}
                checked={visible.has(c.key)}
                onCheckedChange={(v) => {
                  setVisible((prev) => {
                    const next = new Set(prev);
                    if (v) next.add(c.key); else next.delete(c.key);
                    return next;
                  });
                }}
              >{c.label}</DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="bg-white border border-[#E8E6DF] rounded-lg p-4 mb-4 space-y-3">
        <ChipGroup label="Urgency" options={URGENCIES} value={urgency} onChange={setUrgency} testidPrefix="chip-urgency" />
        <ChipGroup label="Topic" options={TOPICS} value={topic} onChange={setTopic} testidPrefix="chip-topic" />
        <ChipGroup label="Status" options={STATUSES} value={status} onChange={setStatus} testidPrefix="chip-status" />
        {activeFilters > 0 && (
          <button
            data-testid="chip-clear"
            onClick={clearFilters}
            className="inline-flex items-center gap-1 text-xs text-[#971a8c] hover:underline"
          >
            <X className="h-3 w-3" /> Clear {activeFilters} filter{activeFilters === 1 ? "" : "s"}
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E8E6DF] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F9F8F6] border-b border-[#E8E6DF]">
                {COLUMNS.filter(c => visible.has(c.key)).map((c) => (
                  <th
                    key={c.key}
                    className="text-left px-4 py-3 font-medium text-xs uppercase tracking-[0.15em] text-[#8B9891] whitespace-nowrap"
                  >{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td className="p-6 text-[#8B9891]" colSpan={COLUMNS.length}>Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td className="p-8 text-[#8B9891]" colSpan={COLUMNS.length}>
                  {activeFilters > 0 ? "No entries match these filters." : "No entries yet. Run a scan from the dashboard to populate the log."}
                </td></tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.entry_id}
                  data-testid={LOGTABLE.row(r.entry_id)}
                  className="border-b last:border-0 border-[#E8E6DF] hover:bg-[#F9F8F6] transition-colors align-top"
                >
                  {visible.has("received_at") && <td className="px-4 py-3 whitespace-nowrap text-[#1C2321]">{fmtDate(r.received_at)}</td>}
                  {visible.has("received_time") && <td className="px-4 py-3 whitespace-nowrap text-[#1C2321]">{fmtTime(r.received_at)}</td>}
                  {visible.has("parent_name") && <td className="px-4 py-3 text-[#1C2321] font-medium min-w-[140px]">{r.parent_name}</td>}
                  {visible.has("parent_email") && <td className="px-4 py-3 text-[#5D6D63] whitespace-nowrap">{r.parent_email}</td>}
                  {visible.has("student_name") && <td className="px-4 py-3 text-[#1C2321] min-w-[140px]">{r.student_name}</td>}
                  {visible.has("subject") && <td className="px-4 py-3 text-[#5D6D63] max-w-[240px]"><div className="line-clamp-2">{r.subject}</div></td>}
                  {visible.has("topic") && <td className="px-4 py-3">{topicBadge(r.topic)}</td>}
                  {visible.has("summary") && <td className="px-4 py-3 text-[#1C2321] max-w-[360px]"><div className="line-clamp-3">{r.summary}</div></td>}
                  {visible.has("action_items") && <td className="px-4 py-3 text-[#5D6D63] max-w-[240px]"><div className="line-clamp-3">{r.action_items || "—"}</div></td>}
                  {visible.has("deadlines") && <td className="px-4 py-3 text-[#5D6D63] max-w-[180px]">{r.deadlines || "—"}</td>}
                  {visible.has("contact_info") && <td className="px-4 py-3 text-[#5D6D63] max-w-[200px]">{r.contact_info || "—"}</td>}
                  {visible.has("urgency") && <td className="px-4 py-3">{urgencyBadge(r.urgency)}</td>}
                  {visible.has("response_status") && (
                    <td className="px-4 py-3">
                      <Select
                        value={r.response_status || "Needs Reply"}
                        onValueChange={(v) => updateEntry(r.entry_id, { response_status: v })}
                      >
                        <SelectTrigger
                          data-testid={LOGTABLE.statusSelect(r.entry_id)}
                          className="h-9 rounded-md border-[#E8E6DF] w-[150px]"
                        ><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Needs Reply">Needs Reply</SelectItem>
                          <SelectItem value="Replied">Replied</SelectItem>
                          <SelectItem value="No Action Needed">No Action Needed</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  )}
                  {visible.has("teacher_notes") && (
                    <td className="px-4 py-3 min-w-[200px]">
                      <Input
                        data-testid={LOGTABLE.notesInput(r.entry_id)}
                        defaultValue={r.teacher_notes || ""}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v !== (r.teacher_notes || "")) updateEntry(r.entry_id, { teacher_notes: v });
                        }}
                        placeholder="Add note…"
                        className="h-9 rounded-md border-[#E8E6DF]"
                      />
                    </td>
                  )}
                  {visible.has("permalink") && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <a
                          data-testid={LOGTABLE.emailLink(r.entry_id)}
                          href={r.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[#971a8c] hover:underline whitespace-nowrap"
                        >
                          Open <ExternalLink className="h-3 w-3" />
                        </a>
                        {(r.response_status || "Needs Reply") === "Needs Reply" && (
                          <div className="inline-flex items-stretch rounded-full border border-[#ec197f] overflow-hidden">
                            <button
                              data-testid={`log-row-${r.entry_id}-draft-btn`}
                              onClick={() => draftReply(r.entry_id)}
                              disabled={draftingId === r.entry_id}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[#ec197f] text-xs font-medium hover:bg-[#fde5ef] transition-colors disabled:opacity-50 whitespace-nowrap"
                              title={`Tone: ${tone}`}
                            >
                              <Sparkles className="h-3 w-3" />
                              {draftingId === r.entry_id ? "Drafting…" : "Draft reply"}
                            </button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  data-testid={`log-row-${r.entry_id}-tone-btn`}
                                  disabled={draftingId === r.entry_id}
                                  aria-label={`Tone: ${tone}. Change`}
                                  className="inline-flex items-center px-1.5 border-l border-[#ec197f] text-[#ec197f] hover:bg-[#fde5ef] transition-colors disabled:opacity-50"
                                >
                                  <ChevronDown className="h-3 w-3" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-64">
                                <DropdownMenuLabel>Reply tone</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuRadioGroup value={tone} onValueChange={setTone}>
                                  {TONES.map((t) => (
                                    <DropdownMenuRadioItem
                                      key={t.key}
                                      value={t.key}
                                      data-testid={`tone-option-${t.key.toLowerCase()}`}
                                      className="flex flex-col items-start gap-0.5 py-2"
                                    >
                                      <span className="text-sm font-medium">{t.label}</span>
                                      <span className="text-xs text-[#8B9891]">{t.hint}</span>
                                    </DropdownMenuRadioItem>
                                  ))}
                                </DropdownMenuRadioGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              data-testid={`log-row-${r.entry_id}-delete-btn`}
                              aria-label="Delete entry"
                              title="Delete entry"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[#8B9891] hover:text-[#E07A5F] hover:bg-[#F9EAEA] transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid={`log-row-${r.entry_id}-delete-confirm`}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Removes the row from your log <strong>and</strong> from your Google Sheet.
                                The original email in Gmail is not touched. Because ContactAssist
                                remembers the message ID, the same email will not be re-added on
                                future scans.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid={`log-row-${r.entry_id}-delete-cancel`}>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                data-testid={`log-row-${r.entry_id}-delete-confirm-btn`}
                                onClick={() => deleteEntry(r.entry_id)}
                                className="bg-[#E07A5F] hover:bg-[#c96a52] text-white"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
